import {
    AnalyticsService,
    IAuthorizedAnalyticsService
} from '~/services/analytics/analytics.service';
import { JStorageService } from '~/services/j-storage.service';
import { BaseUserStore, IUserGetter } from '~/stores/base-user.store';
import { CompanyStore } from '~/stores/company.store';
import { AccountStore } from '~/stores/account.store';
import { BaseSessionStore } from '~/stores/base-session.store';
import { BaseSessionCtrl } from '~/controllers/base-session.controller';
import { BaseSessionAdapter } from '~/services/adapters/base-session.adapter';
import {
    BaseGlobalContext,
    BaseGlobalContextValue
} from '~/providers/base-global-context/base-global.context';
import { WebSocketsService } from '~/controllers/web-sockets.service';
import { WebSocketsAdapter } from '~/services/adapters/web-sockets.adapter';
import { TGlobalContextType } from '~/types/global-context.types';
import { ClientPendingTaskService } from '~/services/client-pending-task.service';
import { ClientPendingTaskController } from '~/controllers/client-pending-task.controller';
import { InjectorService } from '~/services/injector.service';

export interface AuthenticatedGlobalContextValue
    extends BaseGlobalContextValue {
    // Context type
    get globalContextType(): TGlobalContextType;

    // Util services
    get analyticsService(): IAuthorizedAnalyticsService;
    get jStorageService(): JStorageService; // legacy to get data from angular, used in signup and main app. Static class that can be init when needed
    get injectorService(): InjectorService;

    // Auth services
    get accountStore(): AccountStore;
    get companyStore(): CompanyStore;
    get userStore(): IUserGetter;
    get sessionStore(): BaseSessionStore;
    get sessionCtrl(): BaseSessionCtrl;

    // Api services
    get clientPendingTaskService(): ClientPendingTaskService;
    get clientPendingTaskCtrl(): ClientPendingTaskController;
    get sessionService(): BaseSessionAdapter;
    get webSocketsAdapter(): WebSocketsAdapter;
    get webSocketsService(): WebSocketsService;
}

export abstract class AuthenticatedGlobalContext
    extends BaseGlobalContext
    implements AuthenticatedGlobalContextValue
{
    // Context type
    abstract get globalContextType(): TGlobalContextType;

    // Util services
    analyticsService: AnalyticsService;
    jStorageService: JStorageService; // legacy to get data from angular, used in signup and main app. Static class that can be init when needed
    injectorService: InjectorService;

    // Auth services
    accountStore: AccountStore;
    companyStore: CompanyStore;
    protected readonly _userStore: IUserGetter;
    abstract get userStore(): IUserGetter;
    protected readonly _sessionStore: BaseSessionStore;
    abstract get sessionStore(): BaseSessionStore;
    abstract sessionCtrl: BaseSessionCtrl;

    // Api services
    clientPendingTaskService: ClientPendingTaskService;
    clientPendingTaskCtrl: ClientPendingTaskController;
    abstract sessionService: BaseSessionAdapter; // only in vendor side
    webSocketsAdapter: WebSocketsAdapter;
    webSocketsService: WebSocketsService;

    constructor() {
        super();
        // Auth services
        this.accountStore = new AccountStore(this);
        this.companyStore = new CompanyStore(this);
        this._userStore = this.initUserStore();
        this._sessionStore = this.initSessionStore();

        // Util services
        this.injectorService = new InjectorService();
        this.analyticsService = new AnalyticsService({
            configProvider: this.configStore,
            logger: this.fullstoryService,
            userGetter: this._userStore
        });
        this.jStorageService = new JStorageService();

        // Api Service
        this.webSocketsAdapter = new WebSocketsAdapter({
            apiService: this.apiService,
            configProvider: this.configStore
        });
        this.webSocketsService = new WebSocketsService(this);
        this.clientPendingTaskService = new ClientPendingTaskService(
            this.apiService
        );
        this.clientPendingTaskCtrl = new ClientPendingTaskController({
            userGetter: this._userStore,
            clientPendingTaskService: this.clientPendingTaskService,
            webSocketsService: this.webSocketsService
        });
    }

    abstract initUserStore(): BaseUserStore;
    abstract initSessionStore(): BaseSessionStore;
}
